import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown, Eye, FileText, PenLine, Tag } from 'lucide-react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { getAllProductTags, updateProductTags } from '../../../api/api'
import { currencyFormatter } from '../../../utils/utils'
import { Logger } from '../../../logger'

const getMethod = (item) => item?.calibration_method || item?.calibrationMethod || item?.method || item?.calType

/** A single row inside MobileSection. Calibrations get a tappable tag picker
 *  (Pre / In / Post calibration etc.) sourced from getAllProductTags; all rows
 *  show a tappable edit pencil that calls onEdit({ type, product: item }). */
const MobileItemRow = ({ item, type, onEdit, disabled: disabledProp, isNational, isLast }) => {
  const readOnlyUser = useIsReadOnly()
  const disabled = disabledProp || readOnlyUser
  const ccId = useCenterStore((s) => s.ccId)
  const [openTag, setOpenTag] = useState(false)
  const [tagOptions, setTagOptions] = useState([])
  const [currentTag, setCurrentTag] = useState(item?.tag || null)
  const tagRef = useRef(null)

  useEffect(() => setCurrentTag(item?.tag || null), [item?.tag])

  useEffect(() => {
    if (!openTag) return undefined
    const onClickOutside = (e) => {
      if (tagRef.current && !tagRef.current.contains(e.target)) setOpenTag(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [openTag])

  const handleTagClick = useCallback(async () => {
    if (disabled) return
    if (openTag) {
      setOpenTag(false)
      return
    }
    try {
      const res = await getAllProductTags({ ccid: ccId })
      if (res?.status === 200 && Array.isArray(res.data)) {
        setTagOptions(res.data)
        setOpenTag(true)
      }
    } catch (err) {
      Logger.error({ message: err, payload: { file: 'MobileSection > getAllProductTags' } })
    }
  }, [ccId, openTag, disabled])

  const handlePickTag = async (tag) => {
    try {
      await updateProductTags(item.id, tag.id, type)
      setCurrentTag(tag)
      // Mirror desktop behavior of mutating the item so the parent list reads
      // the latest tag without a full refetch.
      item.tag = tag
      setOpenTag(false)
    } catch (err) {
      Logger.error({ message: err, payload: { file: 'MobileSection > updateProductTags' } })
    }
  }

  const method = getMethod(item)
  const qtySuffix = item?.quantity > 1 ? ` x ${item.quantity}` : ''
  // Trailing primary-blue asterisk marks lines mapped by ADAS Intelligence auto-quote
  // (source === 'AUTO_QUOTE') so they read distinctly from MANUAL (STANDARD default +
  // tech-added) lines. Mirrors desktop ItemRow.
  const isAutoQuote = item?.source === 'AUTO_QUOTE'
  const titleText = `${item?.name || ''}${method ? ` (${method})` : ''}${qtySuffix}`
  const isCalibration = type === 'calibration'

  return (
    <div className={`py-3 ${isLast ? '' : 'border-b border-[#EBEDF0]'}`}>
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0 flex-1'>
          <p className='text-[14px] text-[#0A1628]'>
            {titleText || '—'}
            {isAutoQuote && <span className='font-semibold text-[#1F6FEB]'> *</span>}
          </p>

          {isCalibration && (
            <div ref={tagRef} className='relative mt-1.5'>
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation()
                  handleTagClick()
                }}
                disabled={disabled}
                className={`inline-flex items-center gap-1.5 rounded-md bg-[#E8EFFF] px-2 py-1 text-[11px] font-semibold text-[#1F6FEB] disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <span>{currentTag?.name || 'Select tag'}</span>
                <Tag className='h-3 w-3' fill='currentColor' />
                <ChevronDown className='h-3 w-3' />
              </button>
              {openTag && (
                <div className='absolute left-0 top-full z-20 mt-1 min-w-[180px] overflow-hidden rounded-md border border-[#D1D5D8] bg-white shadow-lg'>
                  {tagOptions.length === 0 ? (
                    <div className='px-3 py-2 text-[13px] text-[#6B7280]'>No tags available</div>
                  ) : (
                    tagOptions.map((t) => (
                      <button
                        key={t.id}
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation()
                          handlePickTag(t)
                        }}
                        className={`block w-full px-3 py-2 text-left text-[13px] hover:bg-[#F8FAFC] ${
                          t.id === currentTag?.id ? 'bg-[#E8EFFF] font-semibold text-[#1F6FEB]' : 'text-[#0A1628]'
                        }`}
                      >
                        {t.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className='inline-flex items-center gap-2 whitespace-nowrap'>
          <span className='text-[14px] text-[#0A1628]'>{currencyFormatter(item?.total_price)}</span>
          {!disabled && onEdit && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                onEdit({ type, product: item })
              }}
              aria-label={isNational ? 'View' : 'Edit'}
              className='inline-flex h-6 w-6 items-center justify-center rounded text-[#1F6FEB] hover:bg-[#F1F4F8]'
            >
              {isNational ? <Eye className='h-3.5 w-3.5' /> : <PenLine className='h-3.5 w-3.5' />}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

MobileItemRow.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.string,
  onEdit: PropTypes.func,
  disabled: PropTypes.bool,
  isNational: PropTypes.bool,
  isLast: PropTypes.bool
}

/** Mobile-only collapsible section for Quote/WO summary item lists.
 *  Lavender header (title + amount), simple item rows (name + amount + tag picker + edit + qty),
 *  and a footer "MANAGE …" link. Used in parallel with ExpandableSection (desktop). */
export const MobileSection = ({
  title,
  total,
  items = [],
  manageLabel,
  onManage,
  disabled,
  isNational,
  defaultExpanded = true,
  type,
  onEdit
}) => {
  const [expanded, setExpanded] = useState(!!defaultExpanded)

  return (
    <div className='mb-3 rounded-lg border border-[#D1D5D8] bg-white'>
      <button
        type='button'
        onClick={() => setExpanded((e) => !e)}
        className={`flex w-full items-center justify-between gap-3 bg-[#E8EFFF] px-4 py-3 text-left rounded-t-lg ${expanded ? '' : 'rounded-b-lg'}`}
        aria-expanded={expanded}
      >
        <div className='flex items-center gap-2'>
          <ChevronDown className={`h-4 w-4 text-[#475569] transition-transform ${expanded ? '' : '-rotate-90'}`} strokeWidth={2.25} />
          <span className='text-[14px] font-semibold text-[#0A1628]'>{title}</span>
        </div>
        <span className='text-[14px] font-bold text-[#0A1628]'>{currencyFormatter(total)}</span>
      </button>

      {expanded && (
        <div className='px-4 pb-3 pt-2'>
          {items.length === 0 ? (
            <p className='py-4 text-center text-[13px] text-[#6B7280]'>No items added yet</p>
          ) : (
            items.map((item, idx) => (
              <MobileItemRow
                key={item?.id || idx}
                item={item}
                type={type}
                onEdit={onEdit}
                disabled={disabled}
                isNational={isNational}
                isLast={idx === items.length - 1}
              />
            ))
          )}

          <button
            type='button'
            onClick={onManage}
            disabled={disabled}
            className='mt-2 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] disabled:opacity-40 disabled:cursor-not-allowed'
          >
            <FileText className='h-4 w-4' />
            {manageLabel || `Manage ${title}`}
          </button>
        </div>
      )}
    </div>
  )
}

MobileSection.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number,
  items: PropTypes.array,
  manageLabel: PropTypes.string,
  onManage: PropTypes.func,
  disabled: PropTypes.bool,
  isNational: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  type: PropTypes.string,
  onEdit: PropTypes.func
}
